import axios from 'axios';
import Cookies from 'js-cookie'

const DEFAULT_API_BASE_URL = 'https://demo-destek-api.inohom.cloud';

function normalizeBaseURL(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

function isLocalHostName(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0';
}

function computeBaseURL() {
  const env = process.env.REACT_APP_API_BASE_URL;
  if (env && String(env).trim()) {
    const normalized = normalizeBaseURL(env);
    try {
      const u = new URL(normalized);
      if (typeof window !== 'undefined') {
        const isProdLike = !isLocalHostName(window.location.hostname);
        if (isProdLike && isLocalHostName(u.hostname)) return DEFAULT_API_BASE_URL;
      }
    } catch {}
    return normalized;
  }
  if (typeof window !== 'undefined') {
    try {
      if (isLocalHostName(window.location.hostname)) return window.location.origin;
    } catch {}
  }
  return DEFAULT_API_BASE_URL;
}

const client = axios.create({
  baseURL: computeBaseURL(),
  headers: { 'Content-Type' : 'application/json; charset=UTF-8' },
  paramsSerializer: { indexes: null }
});

const CACHE_ENABLED = false;
const responseCache = new Map();
const resourceIndex = new Map();
const ls = typeof window !== 'undefined' ? window.localStorage : null;
function getSetting(key) {
  if (!ls) return null;
  try { return ls.getItem(key); } catch { return null; }
}

function stableParams(obj) {
  if (!obj) return '';
  const entries = Object.entries(obj).filter(([k,v]) => v !== undefined && v !== null);
  entries.sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([k,v]) => `${k}=${Array.isArray(v) ? v.join(',') : String(v)}`).join('&');
}

function normalizeUrl(base, url) {
  const b = (base || '').replace(/\/$/, '');
  const u = (url || '').replace(/^\//, '');
  return `/${u}`;
}

function resourceFromUrl(url) {
  const path = (url || '').replace(/^https?:\/\/[\s\S]*?\//, '/');
  const seg = path.split('?')[0].split('/').filter(Boolean);
  if (!seg.length) return 'root';
  if (seg[0] === 'logs' && seg[1] === 'tickets') return 'tickets';
  if (seg[0] === 'tickets') return 'tickets';
  if (seg[0] === 'tasks') return 'tasks';
  if (seg[0] === 'customers') return 'customers';
  if (seg[0] === 'employees') return 'employees';
  if (seg[0] === 'service_records') return 'service_records';
  if (seg[0] === 'units' || seg[0] === 'projects') return 'projects';
  if (seg[0] === 'users') return 'users';
  if (seg[0] === 'config') return 'config';
  return seg[0];
}

function makeKey(configOrUrl, cfg) {
  const base = client.defaults.baseURL || '';
  const url = typeof configOrUrl === 'string' ? configOrUrl : configOrUrl.url;
  const params = typeof configOrUrl === 'string' ? (cfg && cfg.params) : (configOrUrl.params || (cfg && cfg.params));
  const norm = normalizeUrl(base, url);
  const sp = stableParams(params);
  return `${norm}?${sp}`;
}

function cacheGet(key) {
  if (!CACHE_ENABLED) return null;
  const disable = getSetting('settings.disableCache') === 'true';
  if (disable) return null;
  if (responseCache.has(key)) return responseCache.get(key);
  if (ls) {
    try {
      const ttlMinStr = getSetting('settings.cacheTTL') || '0';
      const ttlMin = parseInt(ttlMinStr, 10) || 0;
      const raw = ls.getItem(`cache:${key}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (ttlMin > 0) {
          const tsRaw = ls.getItem(`cacheTs:${key}`);
          const ts = tsRaw ? parseInt(tsRaw, 10) : 0;
          const now = Date.now();
          if (!ts || now - ts > ttlMin * 60000) {
            responseCache.delete(key);
            try { ls.removeItem(`cache:${key}`); ls.removeItem(`cacheTs:${key}`); } catch {}
            return null;
          }
        }
        responseCache.set(key, parsed);
        return parsed;
      }
    } catch {}
  }
  return null;
}

function cacheSet(key, data, url) {
  if (!CACHE_ENABLED) return;
  responseCache.set(key, data);
  const res = resourceFromUrl(url);
  if (!resourceIndex.has(res)) resourceIndex.set(res, new Set());
  resourceIndex.get(res).add(key);
  if (ls) {
    try { ls.setItem(`cache:${key}`, JSON.stringify(data)); ls.setItem(`cacheTs:${key}`, String(Date.now())); } catch {}
  }
}

function cacheInvalidate(resource) {
  if (!CACHE_ENABLED) return;
  const keys = resourceIndex.get(resource);
  if (keys && keys.size) {
    for (const k of keys) {
      responseCache.delete(k);
      if (ls) {
        try { ls.removeItem(`cache:${k}`); } catch {}
      }
    }
    resourceIndex.set(resource, new Set());
  }
}

function relatedResources(resource) {
  const map = {
    tickets: ['tickets', 'tasks', 'service_records'],
    tasks: ['tasks', 'tickets', 'service_records'],
    service_records: ['service_records', 'tickets'],
    employees: ['employees', 'teams', 'members'],
    teams: ['teams', 'employees', 'members'],
    members: ['members', 'teams', 'employees'],
    projects: ['projects', 'units', 'tickets'],
    units: ['units', 'projects', 'tickets'],
    customers: ['customers', 'tickets'],
    users: ['users', 'employees'],
    config: ['config'],
  };
  return map[resource] || [resource];
}

function broadcastInvalidate(resource) {
  if (!CACHE_ENABLED) return;
  if (typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent('cache-invalidate', { detail: { resource } })); } catch {}
  }
}

let ws = null;
function ensureWebSocket() {
  if (!CACHE_ENABLED) return;
  if (ws) return;
  try {
    if (typeof window !== 'undefined' && window.navigator && window.navigator.onLine === false) {
      return;
    }
    const api = client.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || '';
    if (!api) return;
    const base = api.replace(/^http/, 'ws').replace(/\/$/, '');
    ws = new WebSocket(base + '/ws/dashboard/');
    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        const ev = data && data.event;
        const res = data && data.resource;
        if (ev === 'resource' && res) {
          cacheInvalidate(String(res));
          if (typeof window !== 'undefined') {
            try { window.dispatchEvent(new CustomEvent('cache-invalidate', { detail: { resource: String(res), reseller: data && data.reseller } })); } catch {}
          }
        }
      } catch {}
    };
  } catch {}
}

if (CACHE_ENABLED) ensureWebSocket();

client.interceptors.request.use(function (config) {
  const token = Cookies.get('token');
  if (token && !config.headers?.Authorization) {
    config.headers = { ...(config.headers || {}), Authorization: "Token " + token };
  }
  return config;
});

client.interceptors.response.use(function (response) {
  if (!CACHE_ENABLED) return response;
  try {
    const method = (response && response.config && response.config.method) || '';
    if (method && method.toLowerCase() !== 'get') {
      const url = response && response.config && response.config.url;
      const res = resourceFromUrl(url || '');
      for (const r of relatedResources(res)) {
        cacheInvalidate(r);
        broadcastInvalidate(r);
      }
    }
  } catch {}
  return response;
}, function (error) {
  if (!CACHE_ENABLED) return Promise.reject(error);
  try {
    const cfg = error && error.config;
    if (cfg) {
      const method = (cfg.method || '').toLowerCase();
      if (method && method !== 'get') {
        const res = resourceFromUrl(cfg.url || '');
        for (const r of relatedResources(res)) {
          cacheInvalidate(r);
          broadcastInvalidate(r);
        }
      }
    }
  } catch {}
  return Promise.reject(error);
});

const originalGet = client.get.bind(client);
client.get = async (url, config = {}) => {
  if (!CACHE_ENABLED) return originalGet(url, config);
  ensureWebSocket();
  const method = (config && config.method) || 'get';
  if (method && method.toLowerCase() !== 'get') return originalGet(url, config);
  const disabledBySetting = getSetting('settings.disableCache') === 'true';
  if (config && config.disableCache) return originalGet(url, config);
  if (disabledBySetting) return originalGet(url, config);
  const key = makeKey(url, config);
  const cached = cacheGet(key);
  if (cached) {
    return Promise.resolve({ data: cached, status: 200, statusText: 'OK', headers: { 'x-cache': 'HIT' }, config: { url, ...config }, request: {} });
  }
  const resp = await originalGet(url, config);
  if (!disabledBySetting) cacheSet(key, resp.data, url);
  return resp;
};

function clearAppCache() {
  if (!CACHE_ENABLED) {
    if (ls) {
      try {
        const keys = [];
        for (let i = 0; i < ls.length; i++) {
          const k = ls.key(i);
          if (k && (k.startsWith('cache:') || k.startsWith('cacheTs:'))) keys.push(k);
        }
        for (const k of keys) ls.removeItem(k);
      } catch {}
    }
    return;
  }
  responseCache.clear();
  resourceIndex.clear();
  if (ls) {
    try {
      const keys = [];
      for (let i = 0; i < ls.length; i++) {
        const k = ls.key(i);
        if (k && (k.startsWith('cache:') || k.startsWith('cacheTs:'))) keys.push(k);
      }
      for (const k of keys) ls.removeItem(k);
    } catch {}
  }
}

export default client;
export { clearAppCache };

if (!CACHE_ENABLED) {
  clearAppCache();
}
